import { useDialogManager } from '../../../../../widgets/dialog';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { UpgradeDialog } from 'src/components/widgets/upgradeDialog/upgradeDialog';
import { stringFormat, T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled from 'styled-components';
import { PropertiesPanelColorControl } from '../scene-properties-panel/propertiesPanelColorProperty';
import { PropertiesPanelSegmented } from '../scene-properties-panel/propertiesPanelSegmentedProperty';
import { PropertiesPanelLookProvider } from '../scene-properties-panel/propertiesPanelLook';
import { PropertiesPanelRangeProperty } from '../scene-properties-panel/propertiesPanelRangeProperty';
import { PropertiesPanelNumberField } from '../scene-properties-panel/propertiesPanelNumberField';
import AutoAwesomeIcon from 'src/shared/material_icons/image/auto_awesome.svg';
import useComposerStore from '../../configurator-composer/composer/composerStore';
import { PropertiesPanelSwitch } from '../scene-properties-panel/propertiesPanelSwitchProperty';
import {
    SettingControl,
    SettingsFieldLabel,
    SettingsFieldRow,
    SettingsGroup,
    SettingsGroupSwitch,
    SettingsMessage,
    SettingsPage,
    SettingsNote,
    SettingsToggleRow
} from '../sceneEditorSettingsLayout';
import { ANTIALIAS_OFF, ANTIALIAS_ON, AntiAliasConfig, AntiAliasDevice, isAntiAliasOn, readAntiAlias, withAntiAliasCell, writeAntiAlias } from './antiAliasing';

/** Le pagine dei Settings che parlano della scena e del prodotto. Interface, AR e VTO hanno le loro. */
export type SettingsEditorSection = 'scene' | 'rendering' | 'behaviours' | 'logics';

interface SettingsEditorProps {
    sceneEditor: any;
    zkScene: any;
    zkProduct?: any;
    section: SettingsEditorSection;
    isAIEnabled?: boolean;
    setAIEnabled?: (enabled: boolean) => void;
    additionalFeatures?: any;
    /** La scena è cambiata: chi ci sta sopra segni che c’è da salvare. */
    onSceneChanged?: () => void;
}

const TooltipLink = styled.a`
    color: ${(props) => props.theme.app.component.link.color};
    text-decoration: underline;
`;

// LA MATRICE DELL'ANTIALIASING (prototipo, `.matrix`): i dispositivi in colonna, vicini e a destra; le righe dicono
// quando smussare i bordi. Il nome prende il resto della riga.
const Matrix = styled.table`
    width: 100%;
    border-collapse: collapse;

    th,
    td {
        padding: 4px 0 4px 12px;
        text-align: center;
        font-weight: 400;
    }

    thead th {
        padding-bottom: 0;
        color: var(--editor-faint, #676a6e);
        font-size: 11px;
        font-weight: 500;
    }

    tbody th {
        width: 100%;
        padding-left: 0;
        color: var(--editor-muted, #55595f);
        font-size: 12px;
        text-align: left;
    }

    td > * {
        margin: 0 auto;
    }
`;

// LO SLIDER DELLA QUALITÀ È QUELLO DELL'INSPECTOR, col suo numero accanto: nel vecchio Backoffice era uno slider.
const QualityBox = styled.div`
    flex: 1;
    max-width: 320px;
`;

// IL GENERATORE AI SI RICONOSCE DAL SUO COLORE (utente, 2026-10-20), come nel Backoffice di prima: il nome col
// gradiente dell'AI di Zakeke e le scintille accanto.
const AiLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-weight: 500;

    > span {
        background: ${(props) => props.theme.ai.gradient};
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    }

    svg {
        width: 15px;
        height: 15px;
        fill: ${(props) => props.theme.ai.gradientMid};
    }
`;

// LE TRE QUANTITÀ IN COLONNA (prototipo, `.set-cols3`): il nome sopra, il numero sotto. Vuoto vuol dire «nessun limite».
// La riga «Deprecated» di Info points: tenue, con la parola in grassetto, dentro la card.
const DeprecatedLine = styled.p`
    margin: 0;
    color: var(--editor-faint, #676a6e);
    font-size: 11px;
    line-height: 1.4;

    strong {
        color: var(--editor-fg, #262a30);
        font-weight: 600;
    }
`;

const QuantityColumns = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
`;

const QuantityColumn = styled.label`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    color: var(--editor-muted, #55595f);
    font-size: 12px;
`;

type InfoPointsColorKey = 'color' | 'borderColor' | 'highlightColor' | 'innerPointColor';

const SettingsEditor: FC<SettingsEditorProps> = ({
    sceneEditor,
    zkScene,
    zkProduct,
    isAIEnabled,
    setAIEnabled,
    additionalFeatures,
    section,
    onSceneChanged
}) => {
    // L'AMBIENTE HA TRASLOCATO. Texture d'ambiente, skybox e la sua sfocatura stanno in Look; qui restano
    // le scelte sulla scena che non si vedono nel palco: come si caricano le texture, come si disegna
    // l'immagine, i comportamenti nel negozio.
    //
    // Resta ospite «Info Points»: la sua casa sarebbero stati i trigger point
    // (docs/scene-editor-new-sections.md § 3), ma verranno deprecati e fino ad allora restano
    // qui con il loro avviso.
    const { showDialog } = useDialogManager();
    const [productQuantity, setProductQuantity] = useComposerStore((state) => [state.productQuantity, state.setProductQuantity]);
    const { sellerMarketplace, updateSubscription } = useSubscriptionData();

    const [infoPointsColors, setInfoPointsColors] = useState<Record<InfoPointsColorKey, string>>({
        color: '#ffffff',
        borderColor: '#ffffff',
        highlightColor: '#ff0000',
        innerPointColor: '#000000'
    });
    const [webpCompressionType, setWebpCompressionType] = useState<string>('Lossy');
    const [webpCompressionQuality, setWebpCompressionQuality] = useState<number>(80);
    const [enableAI, setEnableAI] = useState<boolean>(false);
    const [optimizeTextureManagement, setOptimizeTextureManagement] = useState<boolean>(false);
    const [enableWebP, setEnableWebP] = useState<boolean>(false);
    const [useDelayedMaterialsLoading, setUseDelayedMaterialsLoading] = useState<boolean>(false);
    const [infoPointsEnabled, setInfoPointsEnabled] = useState<boolean>(false);
    const [antiAlias, setAntiAlias] = useState<AntiAliasConfig>(() => readAntiAlias(sceneEditor));

    const setWebPEnabled = useCallback(
        (enabled: boolean) => {
            setEnableWebP(enabled);
            zkScene?.setWebPEnabled?.(enabled);
            onSceneChanged?.();
        },
        [zkScene, onSceneChanged]
    );

    const setWebPType = useCallback(
        (type: string) => {
            setWebpCompressionType(type);
            zkScene?.setWebPType?.(type);
            onSceneChanged?.();
        },
        [zkScene, onSceneChanged]
    );

    const setWebPQuality = useCallback(
        (quality: number) => {
            setWebpCompressionQuality(quality);
            zkScene?.setWebPQuality?.(quality);
            onSceneChanged?.();
        },
        [zkScene, onSceneChanged]
    );

    const setMatchSameNameTextures = useCallback(
        (enabled: boolean) => {
            setOptimizeTextureManagement(enabled);
            sceneEditor?.setMatchSameNameTextures?.(enabled);
            onSceneChanged?.();
        },
        [sceneEditor, onSceneChanged]
    );

    const setUseDelayedMaterialLoading = useCallback(
        (enabled: boolean) => {
            setUseDelayedMaterialsLoading(enabled);
            sceneEditor?.setUseDelayedMaterialLoading?.(enabled);
            onSceneChanged?.();
        },
        [sceneEditor, onSceneChanged]
    );

    const setInfoPointsEnabledHandler = useCallback(
        (enabled: boolean) => {
            setInfoPointsEnabled(enabled);
            sceneEditor!.setInfoPointsEnabled(enabled);
            onSceneChanged?.();
        },
        [sceneEditor, onSceneChanged]
    );

    // L'ANTIALIASING TORNA IN SETTINGS > RENDERING (prototipo V3; utente, 2026-10-19): è una scelta su come il
    // configuratore disegna l'immagine, e in Look stava fra i filtri che si vedono nel palco senza vedersi lui.
    const changeAntiAlias = useCallback(
        (next: AntiAliasConfig) => {
            setAntiAlias(next);
            writeAntiAlias(sceneEditor, next);
            onSceneChanged?.();
        },
        [sceneEditor, onSceneChanged]
    );

    const setAiConfigurationEnabled = useCallback(
        (enabled: boolean) => {
            // Verifica se la feature AI Powered Tools è disponibile
            if (enabled && !additionalFeatures?.aiPoweredTools) {
                showDialog(
                    'Upgrade_Plan',
                    <UpgradeDialog
                        marketPlace={sellerMarketplace ?? undefined}
                        message={stringFormat(T._('Your current plan does not include this feature. Upgrade now to access it.', 'Admin'), 'AI Powered Tools')}
                        onUpgradeCompleted={async () => {
                            updateSubscription();
                        }}
                    />
                );
                return;
            }

            setEnableAI(enabled);
            setAIEnabled?.(enabled);
            onSceneChanged?.();
        },
        [additionalFeatures, showDialog, sellerMarketplace, updateSubscription, setAIEnabled, onSceneChanged]
    );

    // Load scene configuration
    useEffect(() => {
        if (sceneEditor && zkScene) {
            try {
                const config = sceneEditor.getSceneConfig();

                // Load current values from backbone model
                if (config) {
                    // Info Points
                    setInfoPointsEnabled(config.get('infoPoints')?.get('enabled') ?? true);
                    const infoPointsConfig = config.get('infoPoints')?.get('pointsStyle');
                    if (infoPointsConfig) {
                        setInfoPointsColors({
                            color: infoPointsConfig.get?.('color') || '#ffffff',
                            borderColor: infoPointsConfig.get?.('borderColor') || '#ffffff',
                            highlightColor: infoPointsConfig.get?.('highlightColor') || '#ff0000',
                            innerPointColor: infoPointsConfig.get?.('innerPointColor') || '#000000'
                        });
                    }

                    // WebP Compression - usando zkScene
                    setEnableWebP(zkScene?.getWebPEnabled?.() ?? false);
                    setWebpCompressionType(zkScene?.getWebPType?.() ?? 'Lossy');
                    setWebpCompressionQuality(zkScene?.getWebPQuality?.() ?? 80);

                    // Altri settings usando sceneEditor
                    setOptimizeTextureManagement(sceneEditor?.getMatchSameNameTextures?.() ?? false);
                    setUseDelayedMaterialsLoading(sceneEditor?.getUseDelayedMaterialLoading?.() ?? false);

                    setEnableAI(isAIEnabled ?? true);
                }
            } catch (error) {}
        }
    }, [sceneEditor, zkScene]);

    const handleInfoPointsColorChange = useCallback(
        (colorType: InfoPointsColorKey, color: string) => {
            setInfoPointsColors((current) => ({ ...current, [colorType]: color }));
            switch (colorType) {
                case 'color':
                    sceneEditor?.setInfoPointsColor?.(color);
                    break;
                case 'borderColor':
                    sceneEditor?.setInfoPointsBorderColor?.(color);
                    break;
                case 'highlightColor':
                    sceneEditor?.setInfoPointsHighlightColor?.(color);
                    break;
                case 'innerPointColor':
                    sceneEditor?.setInfoPointsInnerPointColor?.(color);
                    break;
            }
            onSceneChanged?.();
        },
        [sceneEditor, onSceneChanged]
    );

    const infoPointsColorRows: { key: InfoPointsColorKey; label: string }[] = [
        { key: 'color', label: T._('Color', 'Admin') },
        { key: 'borderColor', label: T._('Border color', 'Admin') },
        { key: 'highlightColor', label: T._('Highlight color', 'Admin') },
        { key: 'innerPointColor', label: T._('Inner point color', 'Admin') }
    ];

    const savedWithProduct = T._('Saved with the product when you click Save.', 'Admin');

    // SCENE: CIÒ CHE SUCCEDE ALLE TEXTURE QUANDO ENTRANO NELLA SCENA (prototipo V3), con le parole e la spiegazione
    // del Backoffice di prima (utente, 2026-10-20). Spento, il gruppo resta da leggere ma non si tocca. La qualità c'è
    // solo con Lossy, come prima: con Lossless non c'è niente da dosare.
    if (section === 'scene') {
        return (
            <SettingsPage
                title={T._('Scene', 'Admin')}
                lead={stringFormat('{0} {1}', T._('What happens to textures when you upload them.', 'Admin'), savedWithProduct)}
            >
                <SettingsGroup
                    title={T._('WebP for textures', 'Admin')}
                    help={
                        <span>
                            {T._('Enable WebP to optimize texture file sizes. You have two options: ', 'Composer')}
                            <div>
                                <strong>{T._('• Lossy: ', 'Composer')}</strong>
                                {T._('Heavily compresses textures with some quality loss. You can customize the quality level.', 'Composer')}
                            </div>
                            <div>
                                <strong>{T._('• Lossless: ', 'Composer')}</strong>
                                {T._('Minimally compresses textures to maintain original quality.', 'Composer')}
                            </div>
                            <TooltipLink target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/14248548502940-WebP-for-Textures'>
                                {T._('We recommend delving deeper here.', 'Composer')}
                            </TooltipLink>
                        </span>
                    }
                    off={!enableWebP}
                    trailing={
                        <SettingsGroupSwitch
                            id='settings-webp'
                            label={T._('Enable WebP for Textures', 'Admin')}
                            checked={enableWebP}
                            onChange={setWebPEnabled}
                        />
                    }
                >
                    <SettingsFieldRow>
                        <SettingsFieldLabel as='span'>{T._('Compression', 'Admin')}</SettingsFieldLabel>
                        <PropertiesPanelSegmented
                            ariaLabel={T._('Compression', 'Admin')}
                            value={webpCompressionType}
                            options={[
                                { value: 'Lossy', label: T._('Lossy', 'Admin') },
                                { value: 'Lossless', label: T._('Lossless', 'Admin') }
                            ]}
                            onChange={setWebPType}
                        />
                    </SettingsFieldRow>
                    {webpCompressionType === 'Lossy' && (
                        <SettingsFieldRow>
                            <SettingsFieldLabel as='span'>{T._('Quality', 'Admin')}</SettingsFieldLabel>
                            <QualityBox>
                                <PropertiesPanelLookProvider value='scene'>
                                    <PropertiesPanelRangeProperty
                                        id='settings-webp-quality'
                                        label=''
                                        value={webpCompressionQuality}
                                        min={1}
                                        max={100}
                                        step={1}
                                        unit='%'
                                        onChange={(value) => setWebPQuality(Number(value))}
                                    />
                                </PropertiesPanelLookProvider>
                            </QualityBox>
                        </SettingsFieldRow>
                    )}
                </SettingsGroup>
                <SettingsNote>{T._('Applies only to textures uploaded from now on: the ones already in the scene stay as they are.', 'Admin')}</SettingsNote>
            </SettingsPage>
        );
    }

    if (section === 'rendering') {
        const antiAliasOn = isAntiAliasOn(antiAlias);
        const cell = (device: keyof AntiAliasConfig, field: keyof AntiAliasDevice, label: string) => (
            <td>
                <PropertiesPanelSwitch
                    id={`settings-antialias-${device}-${field}`}
                    ariaLabel={label}
                    checked={antiAlias[device][field]}
                    disabled={field === 'inRotationEnabled' && !antiAlias[device].enabled}
                    onChange={(checked) => changeAntiAlias(withAntiAliasCell(antiAlias, device, field, checked))}
                />
            </td>
        );

        return (
            <SettingsPage
                title={T._('Rendering', 'Admin')}
                lead={stringFormat('{0} {1}', T._('Image quality and how the configurator loads the model.', 'Admin'), savedWithProduct)}
            >
                {/* ACCESO DALLA TESTATA VUOL DIRE ACCESO DAVVERO: tutti e due i dispositivi a modello fermo. Spento li
                    spegne tutti e quattro, rotazione compresa. */}
                <SettingsGroup
                    title={T._('Anti-aliasing', 'Admin')}
                    off={!antiAliasOn}
                    trailing={
                        <SettingsGroupSwitch
                            id='settings-antialias'
                            label={T._('Anti-aliasing', 'Admin')}
                            checked={antiAliasOn}
                            onChange={(checked) => changeAntiAlias(checked ? ANTIALIAS_ON : ANTIALIAS_OFF)}
                        />
                    }
                >
                    <Matrix>
                        <thead>
                            <tr>
                                <td />
                                <th scope='col'>{T._('Desktop', 'Admin')}</th>
                                <th scope='col'>{T._('Mobile', 'Admin')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th scope='row'>{T._('When the model is still', 'Admin')}</th>
                                {cell('desktop', 'enabled', T._('Desktop, model still', 'Admin'))}
                                {cell('mobile', 'enabled', T._('Mobile, model still', 'Admin'))}
                            </tr>
                            <tr>
                                <th scope='row'>{T._('While it rotates', 'Admin')}</th>
                                {cell('desktop', 'inRotationEnabled', T._('Desktop, while rotating', 'Admin'))}
                                {cell('mobile', 'inRotationEnabled', T._('Mobile, while rotating', 'Admin'))}
                            </tr>
                        </tbody>
                    </Matrix>
                </SettingsGroup>
                <SettingsGroup title={T._('Textures', 'Admin')}>
                    <SettingsToggleRow
                        id='settings-merge-textures'
                        label={T._('Merge textures with the same name', 'Admin')}
                        sub={T._('Downloaded once, even when several materials use them', 'Admin')}
                        help={T._(
                            'Effortlessly merge textures with identical names into a single texture to optimize performance. When you upload the textures with the same names on multiple materials, Zakeke automatically consolidates them, allowing the browser to download it just once. This optimization minimizes loading times and boosts overall performance, providing a faster and smoother 3D product experience for your customers.',
                            'Admin'
                        )}
                        checked={optimizeTextureManagement}
                        onChange={setMatchSameNameTextures}
                    />
                    <SettingsToggleRow
                        id='settings-delayed-materials'
                        label={T._('Delayed materials loading', 'Admin')}
                        sub={T._('The model shows first, its materials right after', 'Admin')}
                        checked={useDelayedMaterialsLoading}
                        onChange={setUseDelayedMaterialLoading}
                    />
                </SettingsGroup>
            </SettingsPage>
        );
    }

    // LOGICS, SOLO NEL COMPOSER (utente, 2026-10-19): il generatore AI e, sotto, le quantità che il cliente può ordinare.
    //
    // QUANTITIES SI MOSTRA PER FARLO VERIFICARE (utente, 2026-10-20): oggi è una bozza dello store del Composer, che
    // si ricorda fra un'apertura e l'altra ma non arriva al server — `quantityRule` è in sola lettura in GraphQL. La
    // si scrive anche sul prodotto, così il giorno che il salvataggio la porta il dato è già lì. Da segnalare ai dev.
    if (section === 'logics') {
        const quantity: MPlaza.QuantityRule | null = productQuantity ?? zkProduct?.get?.('quantityRule') ?? null;
        const writeQuantity = (next: MPlaza.QuantityRule | null) => {
            setProductQuantity(next);
            zkProduct?.set?.('quantityRule', next);
            onSceneChanged?.();
        };
        // VUOTO È «NESSUN LIMITE». Il minimo non supera il massimo e il massimo non scende sotto il minimo: chi scrive
        // l'uno oltre l'altro trascina con sé l'altro.
        const changeQuantity = (field: 'minQuantity' | 'maxQuantity' | 'step', raw: string) => {
            if (!quantity) return;
            const value = raw === '' ? undefined : Math.max(1, Math.round(Number(raw)));
            const next = quantity.clone();
            (next as any).set(field, value);
            const min = next.get('minQuantity');
            const max = next.get('maxQuantity');
            if (min && max && min > max) (next as any).set(field === 'minQuantity' ? 'maxQuantity' : 'minQuantity', value);
            writeQuantity(next);
        };
        // IL CAMPO NUMERICO DELL'EDITOR (prototipo, utente 2026-10-20): si trascina, ha le frecce, e può restare vuoto —
        // vuoto è «nessun limite», col segnaposto che lo dice.
        const quantityField = (field: 'minQuantity' | 'maxQuantity' | 'step', label: string, emptyLabel: string) => {
            const current = (quantity as any)?.get(field) as number | undefined;
            return (
                <QuantityColumn as='div'>
                    {label}
                    <PropertiesPanelNumberField
                        full
                        value={current ?? 1}
                        empty={current == null}
                        placeholder={emptyLabel}
                        min={1}
                        step={1}
                        ariaLabel={label}
                        onChange={(value) => changeQuantity(field, String(value))}
                        onEmpty={() => changeQuantity(field, '')}
                    />
                </QuantityColumn>
            );
        };

        return (
            <SettingsPage
                title={T._('Logics', 'Admin')}
                lead={stringFormat('{0} {1}', T._('What the customer can order, and help while you build features and choices.', 'Admin'), savedWithProduct)}
            >
                <SettingsGroup title={T._('Automation', 'Admin')}>
                    <SettingsToggleRow
                        id='settings-ai-generator'
                        label={
                            <AiLabel>
                                <span>{T._('AI Product Configuration Generator', 'Admin')}</span>
                                <AutoAwesomeIcon aria-hidden='true' />
                            </AiLabel>
                        }
                        sub={T._('Your customers describe what they want and the configuration is generated for them.', 'Admin')}
                        help={
                            <>
                                {T._(
                                    "Enhance your product customization experience with our AI-Powered Configuration Generator. Your clients simply describe their desired configuration and our advanced AI technology automatically generates the perfect customized product for them. No manual option selection, our intelligent system handles the permutations and delivers a tailored configuration that precisely matches your customers' preferences.",
                                    'Composer'
                                )}
                                <br />
                                <TooltipLink target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/9649789714460-AI-Configuration'>
                                    {T._('Learn more', 'Composer')}
                                </TooltipLink>
                            </>
                        }
                        checked={enableAI}
                        onChange={setAiConfigurationEnabled}
                    />
                </SettingsGroup>
                <SettingsGroup
                    title={T._('Quantities', 'Admin')}
                    off={!quantity}
                    trailing={
                        <SettingsGroupSwitch
                            id='settings-quantities'
                            label={T._('Allow the user to change quantities', 'Admin')}
                            checked={!!quantity}
                            onChange={(checked) => writeQuantity(checked ? new MPlaza.QuantityRule() : null)}
                        />
                    }
                >
                    <SettingsMessage>{T._('Let the customer change how many they order.', 'Admin')}</SettingsMessage>
                    <QuantityColumns>
                        {quantityField('minQuantity', T._('Minimum', 'Admin'), T._('None', 'Admin'))}
                        {quantityField('maxQuantity', T._('Maximum', 'Admin'), T._('No limit', 'Admin'))}
                        {quantityField('step', T._('Step', 'Admin'), '1')}
                    </QuantityColumns>
                </SettingsGroup>
            </SettingsPage>
        );
    }

    return (
        <SettingsPage
            title={T._('Behaviors', 'Admin')}
            lead={stringFormat('{0} {1}', T._('How the product behaves in your store.', 'Admin'), savedWithProduct)}
        >
            <SettingsGroup
                title={T._('Info points', 'Admin')}
                off={!infoPointsEnabled}
                trailing={
                    <SettingsGroupSwitch
                        id='settings-info-points'
                        label={T._('Info points', 'Admin')}
                        checked={infoPointsEnabled}
                        onChange={setInfoPointsEnabledHandler}
                    />
                }
            >
                {/* «DEPRECATED» È UNA RIGA DELLA CARD, nello stile dell'editor (utente, 2026-10-20): prima era il banner del
                    Backoffice, l'unico elemento della pagina fuori dal suo stile. */}
                <DeprecatedLine>
                    <strong>{T._('Deprecated', 'Admin')}</strong> {T._('Info points will be deprecated soon, so we recommend not using them.', 'Admin')}
                </DeprecatedLine>
                <SettingsMessage>
                    {T._('High-resolution 2D images of the product, shown on the 3D model.', 'Admin')}{' '}
                    <TooltipLink href='https://zakeke.zendesk.com/hc/en-us/articles/360014772579' target='_blank'>
                        {T._('Learn more', 'Admin')}
                    </TooltipLink>
                </SettingsMessage>
                {infoPointsColorRows.map(({ key, label }) => (
                    <SettingsFieldRow key={key}>
                        <SettingsFieldLabel htmlFor={`settings-info-points-${key}`}>{label}</SettingsFieldLabel>
                        <SettingControl>
                            <PropertiesPanelColorControl
                                id={`settings-info-points-${key}`}
                                value={infoPointsColors[key]}
                                onChange={(color) => handleInfoPointsColorChange(key, color)}
                            />
                        </SettingControl>
                    </SettingsFieldRow>
                ))}
            </SettingsGroup>
        </SettingsPage>
    );
};

export default SettingsEditor;
